import { useCallback, useMemo } from 'react';
import { Segmented } from 'antd';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type {
  FindingGroup,
  FindingGroupStyle,
  FindingLabel,
} from '@/models/template';
import sectionStyles from '@/features/report/Findings/FindingGroupSection/FindingGroupSection.module.css';
import { AddButton } from '../primitives/AddButton';
import { EditableText } from '../primitives/EditableText';
import { indexToLetter } from '../primitives/format';
import { EditableSplitFindingRow } from './EditableSplitFindingRow';
import { EditableGroupedFindingBlock } from './EditableGroupedFindingBlock';
import styles from './EditableFindingsSection.module.css';

interface EditableFindingGroupProps {
  group: FindingGroup;
  position: number;
  findingClasses: string[];
  usedClasses: Set<string>;
  onChange: (group: FindingGroup) => void;
}

export function EditableFindingGroup({
  group,
  position,
  findingClasses,
  usedClasses,
  onChange,
}: EditableFindingGroupProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const sortedRows = useMemo(
    () => [...group.findingLabels].sort((a, b) => a.index - b.index),
    [group.findingLabels]
  );

  const handleRowChange = useCallback(
    (idx: number, next: FindingLabel) => {
      const copy = [...sortedRows];
      copy[idx] = next;
      onChange({ ...group, findingLabels: copy });
    },
    [group, onChange, sortedRows]
  );

  const handleRowDelete = useCallback(
    (idx: number) => {
      onChange({
        ...group,
        findingLabels: sortedRows.filter((_, i) => i !== idx),
      });
    },
    [group, onChange, sortedRows]
  );

  const handleAddRow = useCallback(() => {
    const maxIndex = sortedRows.reduce((m, fl) => Math.max(m, fl.index), -1);
    const now = new Date().toISOString();
    const newLabel: FindingLabel = {
      uuid: crypto.randomUUID(),
      label: '',
      findingClass: '',
      index: maxIndex + 1,
      createdAt: now,
      updatedAt: now,
    };
    onChange({ ...group, findingLabels: [...sortedRows, newLabel] });
  }, [group, onChange, sortedRows]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIdx = sortedRows.findIndex((r) => r.uuid === active.id);
      const newIdx = sortedRows.findIndex((r) => r.uuid === over.id);
      if (oldIdx === -1 || newIdx === -1) return;
      const reordered = arrayMove(sortedRows, oldIdx, newIdx).map((r, i) => ({
        ...r,
        index: i,
      }));
      onChange({ ...group, findingLabels: reordered });
    },
    [group, onChange, sortedRows]
  );

  const handleStyleChange = (value: FindingGroupStyle) => {
    onChange({ ...group, style: value });
  };

  return (
    <div className={sectionStyles.section}>
      <div className={sectionStyles.header}>
        <span className={sectionStyles.accentBar} />
        <span className={sectionStyles.title}>
          {position}.{' '}
          <EditableText
            value={group.label}
            onChange={(label) => onChange({ ...group, label })}
            placeholder="Group name"
          />
        </span>
        <Segmented
          size="small"
          className={styles.styleToggle}
          value={group.style === 'SPLIT' ? 'SPLIT' : 'GROUPED'}
          onChange={(v) => handleStyleChange(v as FindingGroupStyle)}
          options={[
            { value: 'GROUPED', label: 'Grouped' },
            { value: 'SPLIT', label: 'Split' },
          ]}
        />
      </div>
      {group.style === 'SPLIT' ? (
        <>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortedRows.map((r) => r.uuid)}
              strategy={verticalListSortingStrategy}
            >
              <div className={sectionStyles.content}>
                {sortedRows.map((fl, idx) => (
                  <EditableSplitFindingRow
                    key={fl.uuid}
                    label={fl}
                    position={indexToLetter(idx)}
                    findingClasses={findingClasses}
                    usedClasses={usedClasses}
                    onChange={(next) => handleRowChange(idx, next)}
                    onDelete={() => handleRowDelete(idx)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
          <div className={styles.addRowWrap}>
            <AddButton onClick={handleAddRow} label="Add finding" />
          </div>
        </>
      ) : (
        <EditableGroupedFindingBlock
          group={group}
          findingClasses={findingClasses}
          usedClasses={usedClasses}
          onChange={onChange}
        />
      )}
    </div>
  );
}
